import type {
    CatalogImportNameCandidate,
    CategoryListItem,
} from '@/api/models';

/** One choice in a Names picker. `id` is the picker key; `label` is what it shows and searches. */
export type NameOption = {
    id: string;
    label: string;
    /** Another new text of the file, not an entity of the catalogue. */
    isFileText?: boolean;
};

/** Guards a malformed parent chain; the catalogue is far shallower. */
const MAX_DEPTH = 32;

const byLabel = (a: NameOption, b: NameOption): number =>
    a.label.localeCompare(b.label);

/** Named entities (brands, suppliers) as options, by name. */
export const namedOptionsOf = (
    items: readonly { id: string; name: string }[],
): NameOption[] =>
    items.map(({ id, name }) => ({ id, label: name })).sort(byLabel);

/**
 * Categories as options labelled with their path (`Electronics › Headphones`),
 * so two categories of the same name under different parents tell apart, and
 * a search for the parent finds its children. Sorted by path.
 */
export const categoryOptionsOf = (
    items: readonly CategoryListItem[],
): NameOption[] => {
    const byId = new Map(items.map((item) => [item.id, item]));
    const pathOf = (item: CategoryListItem): string => {
        const names = [item.name];
        let parentId = item.parentId;
        for (let depth = 0; parentId !== null && depth < MAX_DEPTH; depth++) {
            const parent = byId.get(parentId);
            if (!parent) {
                break;
            }
            names.unshift(parent.name);
            parentId = parent.parentId;
        }
        return names.join(' › ');
    };
    return items
        .map((item) => ({ id: item.id, label: pathOf(item) }))
        .sort(byLabel);
};

/**
 * An ambiguous name's candidates as choices, in the server's order: a
 * category with its parent (`Electronics › Apple`), which is what tells two
 * of the same name apart; a brand or supplier by its name as stored, whose
 * case is all that differs.
 */
export const candidateOptionsOf = (
    candidates: readonly CatalogImportNameCandidate[],
): NameOption[] =>
    candidates.map(({ id, name, parentName }) => ({
        id,
        label: parentName ? `${parentName} › ${name}` : name,
    }));
